
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/components/auth/AuthProvider';
import { getStableSession } from '@/lib/getStableSession';
import { getSupabase } from '@/lib/supabaseClient';

interface RoleGuardProps {
  children: React.ReactNode;
  requiredRole?: 'admin' | 'manager' | 'staff' | 'employee';
  requireAdmin?: boolean;
  fallbackPath?: string;
}

export const RoleGuard = ({ 
  children, 
  requiredRole, 
  requireAdmin = false,
  fallbackPath = '/login' 
}: RoleGuardProps) => {
  // Always call hooks at the top, unconditionally
  const { session, profile, isAdmin, isManager, loading } = useAuth();
  const location = useLocation();
  const [stableSession, setStableSession] = useState<any>(null);
  const [sessionChecked, setSessionChecked] = useState(false);

  // Stabilize session to prevent rapid redirects
  useEffect(() => {
    let cancelled = false;
    
    const checkSession = async () => {
      console.log('🔍 RoleGuard: Checking stable session...');
      const supabase = getSupabase();
      const stable = await getStableSession(supabase, { retries: 2, delay: 100 });
      
      if (!cancelled) {
        setStableSession(stable);
        setSessionChecked(true);
        console.log('🔍 RoleGuard: Session check complete', { 
          hasSession: !!stable, 
          profileRole: profile?.role,
          pathname: location.pathname 
        });
      }
    };

    if (!sessionChecked) {
      checkSession();
    }

    return () => { cancelled = true; };
  }, [sessionChecked, profile?.role, location.pathname]);

  // Wait for both auth loading and stable session check
  if (loading || !sessionChecked) {
    console.log('🔍 RoleGuard: Still loading...', { loading, sessionChecked });
    return null;
  }

  // Check for SMS staff authentication in localStorage
  const smsAuth = localStorage.getItem('staff_sms_auth');
  const smsAuthData = smsAuth ? JSON.parse(smsAuth) : null;
  const isSmsAuthenticated = smsAuthData && smsAuthData.expiresAt && new Date(smsAuthData.expiresAt) > new Date();

  // Use stable session for redirects to prevent loops
  const activeSession = stableSession || session;

  // If there's no stable session and no SMS auth, redirect to SMS login
  if (!activeSession && !isSmsAuthenticated) {
    console.log('🔍 RoleGuard: No session or SMS auth, redirecting to SMS login');
    return <Navigate to={fallbackPath} replace />;
  }

  // If user has SMS auth but no Supabase session, allow access for staff-level content
  if (isSmsAuthenticated && !activeSession) {
    // Only allow access to staff/employee level content
    if (requireAdmin || (requiredRole && requiredRole !== 'staff' && requiredRole !== 'employee')) {
      console.log('🔍 RoleGuard: SMS auth insufficient for admin/manager access');
      return <Navigate to="/auth" replace />;
    }
    
    // Allow SMS authenticated users to access staff content
    if (location.pathname === '/') {
      return <Navigate to="/staff-dashboard" replace />;
    }
    
    return <>{children}</>;
  }

  // If role-based access is required but profile hasn't loaded yet, wait
  if ((requireAdmin || requiredRole) && !profile) {
    return null;
  }

  // SECURITY: Only use server-verified roles
  if (requireAdmin && !isAdmin) {
    return <Navigate to={fallbackPath} replace />;
  }

  // Handle initial post-login redirects when user lands on root
  if (profile && location.pathname === '/') {
    if (profile.role === 'admin' || profile.role === 'manager') {
      return <Navigate to="/security-level-picker" replace />;
    } else {
      return <Navigate to="/staff-dashboard" replace />;
    }
  }

  // If specific role is required, check against server-verified role only
  if (requiredRole) {
    const hasRequiredRole = () => {
      switch (requiredRole) {
        case 'admin':
          return isAdmin;
        case 'manager':
          return isManager;
        case 'staff':
        case 'employee':
          return profile !== null; // All authenticated users with profiles can access employee/staff content
        default:
          return false;
      }
    };

    if (!hasRequiredRole()) {
      return <Navigate to={fallbackPath} replace />;
    }
  }

  return <>{children}</>;
};
